import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form.ts';
import { type DealFormData, dealFormField, pmpDealType } from '@openx/types/deal.ts';
import { BUDGET_TYPE, type MonetizingRegion } from '@openx/types/monetizing.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { type JSX, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getDealsLabels } from '../../utils/dealFormUtils.ts';
import { useDealStartDateCheck } from './hooks/useDealStartDateCheck.ts';
import { usePacingEffect } from './hooks/usePacingEffect/usePacingEffect.tsx';
import { PacingFormDate } from './PacingFormDate.tsx';
import { PacingFormFields } from './PacingFormFields.tsx';
import { PacingFormSwitch } from './PacingFormSwitch.tsx';

type PacingSectionProps = {
  isEdit: boolean;
  ignoreDealType?: boolean;
  isHidden?: boolean;
  isLoading?: boolean;
};

export const PacingSection = ({
  isEdit,
  ignoreDealType,
  isHidden = false,
  isLoading = false,
}: PacingSectionProps): JSX.Element => {
  const { t } = useTranslation();

  const {
    setValue,
    trigger,
    formState: { isSubmitting },
  } = useFormContext<DealFormData>();

  const { isPub } = usePermissionContext();

  const observableDealType = useWatch({ name: dealFormField.PMP_DEAL_TYPE });
  const observableIsPacingEnabled = useWatch({ name: dealFormField.IS_PACING_ENABLED });
  const observableMonetizingMode = useWatch({ name: dealFormField.PACING_MODE });
  const observableBudgetType = useWatch({ name: dealFormField.BUDGET_TYPE });
  const observableBudgetMode = useWatch({ name: dealFormField.BUDGET_MODE });

  const { isStartDateOneHourAhead } = useDealStartDateCheck({ isEdit });

  const dealsLabels = useMemo(() => getDealsLabels(t), [t]);

  const isPacingSupportedDeal = !!(
    observableDealType === pmpDealType.Guaranteed ||
    observableDealType === pmpDealType.PreferredDeal ||
    (isPub && observableDealType === pmpDealType.PrivateAuction) ||
    ignoreDealType
  );

  const regionOptions: MonetizingRegion[] = ['US', 'EU', 'ASIA'];

  const budgetLabel =
    observableBudgetType === BUDGET_TYPE.SPEND ? dealsLabels.monetizing_budget : dealsLabels.monetizing_impression_goal;

  usePacingEffect();
  return (
    <FormSection
      divider
      title={isHidden ? t('Deal Delivery') : t('Pacing and Duration')}
      description={!isHidden && t('A metering tool that controls how quickly (or not) a deal monetizes.')}
      rightColumnSize={RIGHT_COLUMN_WIDTH}
    >
      {!isHidden && (
        <>
          <PacingFormSwitch
            isSubmitting={isSubmitting}
            label={dealsLabels[dealFormField.IS_PACING_ENABLED]}
            isStartDateOneHourAhead={isStartDateOneHourAhead}
            isPacingSupportedDeal={isPacingSupportedDeal}
            isPmpDealType={observableDealType === pmpDealType.Guaranteed}
          />

          {observableIsPacingEnabled &&
            (isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} data-test="pacing-loader" />
              </Box>
            ) : (
              <PacingFormFields
                isEdit={isEdit}
                canChangeRegion={!isEdit || isStartDateOneHourAhead}
                canChangeGoal={!isEdit || isStartDateOneHourAhead}
                canChangeBudgetMode={!isEdit || isStartDateOneHourAhead}
                budgetLabel={budgetLabel}
                regionLabel={dealsLabels[dealFormField.MONETIZING_REGION]}
                regionOptions={regionOptions}
              />
            ))}
        </>
      )}
      <PacingFormDate
        startLabel={dealsLabels[dealFormField.START_DATE]}
        endLabel={dealsLabels[dealFormField.END_DATE]}
        setValue={setValue}
        trigger={trigger}
        isEdit={isEdit}
        dealType={observableDealType}
        isPacingEnabled={observableIsPacingEnabled}
        monetizingMode={observableMonetizingMode}
        budgetMode={observableBudgetMode}
        isStartDateOneHourAhead={isStartDateOneHourAhead}
        isPacingHidden={isHidden}
      />
    </FormSection>
  );
};
